import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Environment } from '../../models/Environment';

/**
 * Build identity vs. build behaviour.
 *
 * `environment.name` ('production' | 'development' | 'local') is the single source of
 * BUILD IDENTITY, fixed at compile time by the angular.json configuration.
 *
 * BEHAVIOUR (service worker, Sentry, environment selector) is driven by explicit
 * `environment.*` flags, NOT re-derived from `name`. Overloading one signal (the old
 * `production` flag) to mean several things is exactly what this refactor removes, so the
 * flags stay explicit and this file is the only place that maps identity/flags to behaviour.
 *
 * Pure functions are exported for pre-bootstrap use in `main.ts` (before Angular DI exists);
 * `EnvironmentService` wraps them for injection into components and services.
 */

export function isProductionBuild(env: Environment = environment): boolean {
  return env.production === true;
}

export function isDevelopmentBuild(env: Environment = environment): boolean {
  return env.name === 'development';
}

export function isLocalBuild(env: Environment = environment): boolean {
  return env.name === 'local';
}

/** Service worker (push) must run on dev/local as well as prod — see SUN-725. */
export function shouldEnableServiceWorker(env: Environment = environment): boolean {
  return env.enableServiceWorker ?? env.production ?? false;
}

/** Sentry runs on prod + deployed dev; off for local builds. */
export function shouldEnableSentry(env: Environment = environment): boolean {
  return env.enableSentry ?? env.production ?? false;
}

/** Governs both visibility of the runtime API selector and the ability to switch. */
export function shouldShowEnvironmentSelector(env: Environment = environment): boolean {
  return env.showEnvironmentSelector ?? !env.production;
}

@Injectable({ providedIn: 'root' })
export class EnvironmentService {
  get name(): string {
    return environment.name;
  }

  isProductionBuild(): boolean {
    return isProductionBuild();
  }

  isDevelopmentBuild(): boolean {
    return isDevelopmentBuild();
  }

  isLocalBuild(): boolean {
    return isLocalBuild();
  }

  shouldEnableServiceWorker(): boolean {
    return shouldEnableServiceWorker();
  }

  shouldEnableSentry(): boolean {
    return shouldEnableSentry();
  }

  showEnvironmentSelector(): boolean {
    return shouldShowEnvironmentSelector();
  }
}
